import { Layout as AntLayout } from "antd";
import { Link } from "../../../../router/LocaleLink";
import classNames from "classnames";
import { motion } from "framer-motion";
import { MenuFoldOutlined, MenuUnfoldOutlined } from "@ant-design/icons";
import { useShouldHideSubscriptions } from "../../../../hooks/useShouldHideSubscriptions";
import { Paths } from "../../../../router/paths";
import { Menu, type MenuItem } from "./Menu";
import { getMenuItemsForRole } from "./menuItems";
import { useAuth } from "../../../../api/auth/useAuth";
import { useSidebar } from "./useSidebar";
import { MobileSidebar } from "./MobileSidebar";
import { trackSidebarSectionClicked } from "../../../../analytics/amplitudeTracking";
import { getSidebarMenuItemsWithMeta } from "../../../../analytics/sidebarMenuTracking";
import styles from "./index.module.css";

export const Sidebar = () => {
  const { user } = useAuth();
  const { isCollapsed, toggleSidebar } = useSidebar();
  const hideSubscriptions = useShouldHideSubscriptions();
  const roleName = user?.role?.name;
  const roleMenuGroups = getMenuItemsForRole(roleName, {
    hideSubscriptions,
  });
  const sidebarMenuMetaByKey = new Map(
    getSidebarMenuItemsWithMeta(roleName).map((entry) => [
      entry.item.key,
      entry,
    ]),
  );

  const handleSidebarItemClick = (
    item: MenuItem,
    meta: { section_position: number; section_category: string },
  ) => {
    const resolved = sidebarMenuMetaByKey.get(item.key);
    trackSidebarSectionClicked({
      section_name: item.labelKey,
      section_position: resolved?.section_position ?? meta.section_position,
      section_category: resolved?.section_category ?? meta.section_category,
    });
  };

  const { Sider } = AntLayout;

  // Both variants are rendered and CSS shows one per viewport (1167px), so the
  // server HTML is right for phones and desktops alike.
  return (
    <>
      <div className={styles.mobileOnly}>
        <MobileSidebar />
      </div>
      <div className={styles.desktopOnly}>
        <Sider
          width={isCollapsed ? 80 : 260}
          theme="light"
          className={classNames(styles.sidebar, {
            [styles.collapsedSidebar]: isCollapsed,
          })}
          style={{ transition: "width 0.3s ease" }}
        >
          <div className={styles.sidebarColumn}>
            <div className={styles.contentBlock}>
              <div className={styles.menuScrollArea}>
                <div className={isCollapsed ? styles.logoCollapsed : styles.logo}>
                  <Link to={Paths.HOME}>
                    <motion.img
                      src={isCollapsed ? "/Logomark.webp" : "/Logo.svg"}
                      alt="logo"
                      className={`${styles.logoImage} ${
                        isCollapsed ? styles.logoImageCollapsed : ""
                      }`}
                      animate={{
                        width: isCollapsed ? 32 : "auto",
                        height: isCollapsed ? 32 : "auto",
                      }}
                      transition={{
                        duration: 0.3,
                        ease: "easeInOut",
                      }}
                    />
                  </Link>
                </div>
                <motion.div
                  animate={{
                    opacity: 1,
                  }}
                  transition={{ duration: 0.2 }}
                >
                  {roleMenuGroups.map((item, index) => (
                    <Menu
                      key={item[0]?.key || index}
                      items={item}
                      isCollapsed={isCollapsed}
                      onItemClick={handleSidebarItemClick}
                    />
                  ))}
                </motion.div>
              </div>
            </div>
          </div>

          <motion.div
            className={styles.toggleButton}
            onClick={toggleSidebar}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            {isCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          </motion.div>
        </Sider>
      </div>
    </>
  );
};
